import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { useLanguage } from '@/context/LanguageContext';
import { toast } from 'sonner';
import { 
  Check, Crown, Sparkles, Clock, X, AlertCircle, Gift, ChevronDown, ChevronUp,
  Users, UserPlus, Trash2, Mail, Building2, MessageSquare, Share2, Ticket, Loader2
} from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { isIOS } from '@/lib/platform';
import IapPurchaseBlock from '@/components/IapPurchaseBlock';

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center justify-between py-2 cursor-pointer hover:text-primary transition-colors">
          <h3 className="font-medium text-left">{question}</h3>
          {isOpen ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-2" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-2" />
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <p className="text-sm text-muted-foreground pb-3">
          {answer}
        </p>
      </CollapsibleContent>
    </Collapsible>
  );
};

const TIERS = {
  individual: {
    name: 'Individual',
    nameEs: 'Individual',
    price: 1.99,
    maxUsers: 1,
    description: 'Perfect for personal use',
    descriptionEs: 'Perfecto para uso personal',
    icon: Crown
  },
  family: {
    name: 'Family & Friends',
    nameEs: 'Familia y Amigos',
    price: 2.99,
    maxUsers: 4,
    description: 'Share with up to 4 users',
    descriptionEs: 'Comparte con hasta 4 usuarios',
    icon: Users
  },
  cookout: {
    name: 'The Whole Cookout',
    nameEs: 'Todo el Asado',
    price: 5.99,
    maxUsers: 8,
    description: 'Share with up to 8 users',
    descriptionEs: 'Comparte con hasta 8 usuarios',
    icon: Users
  },
  reunion: {
    name: 'Family Reunion',
    nameEs: 'Reunión Familiar',
    price: 12.99,
    maxUsers: 20,
    description: 'Share with up to 20 users',
    descriptionEs: 'Comparte con hasta 20 usuarios',
    icon: Users
  }
};

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { t, isSpanish } = useLanguage();
  const { 
    subscriptionStatus, 
    cancelSubscription,
    getRemainingFreeViews,
    FREE_PHRASE_LIMIT,
    fetchStatus
  } = useSubscription();
  
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState('individual');
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [myInvites, setMyInvites] = useState([]);
  const [promoCode, setPromoCode] = useState('');
  const [promoValidation, setPromoValidation] = useState(null);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);

  // iOS users must purchase via Apple In-App Purchase per App Store
  // guideline 3.1.1. Detect once on mount and pivot the entire UI.
  const isAppleStoreContext = isIOS();

  // Fetch members if user is subscription owner
  useEffect(() => {
    if (isAuthenticated && subscriptionStatus?.is_owner && subscriptionStatus?.status !== 'free') {
      fetchMembers();
    }
    if (isAuthenticated) {
      fetchMyInvites();
    }
  }, [isAuthenticated, subscriptionStatus]);

  const fetchMembers = async () => {
    try {
      const { data, error } = await apiFetch('/api/subscription/members');
      if (!error && data) {
        setMembers(data.members || []);
        setPendingInvites(data.pending_invites || []);
      }
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  };

  const fetchMyInvites = async () => {
    try {
      const { data, error } = await apiFetch('/api/subscription/invites');
      if (!error && data) {
        setMyInvites(data.invites || []);
      }
    } catch (error) {
      console.error('Failed to fetch invites:', error);
    }
  };

  // Validate promo code
  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return;
    
    setIsValidatingPromo(true);
    try {
      const { data, error } = await apiFetch(`/api/promo-codes/validate/${promoCode.toUpperCase()}`);
      setPromoValidation(data || { valid: false, message: error || 'Failed to validate code' });
      
      if (data?.valid) {
        toast.success(data.message);
      }
    } catch (error) {
      setPromoValidation({ valid: false, message: 'Failed to validate code' });
    } finally {
      setIsValidatingPromo(false);
    }
  };

  // Apply promo code
  const handleApplyPromo = async () => {
    if (!promoValidation?.valid) {
      toast.error(isSpanish ? 'Por favor valida el código primero' : 'Please validate the code first');
      return;
    }
    if (!user?.user_id) {
      toast.error(isSpanish ? 'Inicia sesión para aplicar un código' : 'Please sign in to apply a code');
      return;
    }
    
    setIsApplyingPromo(true);
    try {
      const { data, error } = await apiFetch('/api/promo-codes/redeem', {
        method: 'POST',
        body: {
          code: promoCode.toUpperCase(),
          user_id: user.user_id
        }
      });
      
      if (error) {
        // Surface the exact backend error (e.g. "You have already used this promo code")
        toast.error(error);
        setPromoValidation({ valid: false, message: error });
        return;
      }
      
      toast.success(data?.message || (isSpanish ? '¡Código aplicado!' : 'Code applied!'));
      setPromoCode('');
      setPromoValidation(null);
      fetchStatus();
    } catch (err) {
      toast.error(err?.message || (isSpanish ? 'Error al aplicar el código' : 'Failed to apply code'));
    } finally {
      setIsApplyingPromo(false);
    }
  };

  const handleSubscribe = async (tier) => {
    if (!isAuthenticated) {
      toast.info(isSpanish ? 'Por favor inicia sesión para suscribirte' : 'Please sign in to subscribe');
      navigate('/login');
      return;
    }
    
    setIsLoading(true);
    try {
      const { data, error } = await apiFetch('/api/subscription/create-checkout', {
        method: 'POST',
        body: { 
          origin_url: window.location.origin,
          tier: tier
        }
      });
      
      if (error) {
        throw new Error(error || 'Failed to create checkout');
      }
      
      if (data.url) {
        window.location.replace(data.url);
      }
    } catch (error) {
      toast.error(error.message);
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    const confirmMsg = isSpanish 
      ? '¿Estás seguro de que quieres cancelar tu suscripción? Mantendrás acceso hasta el final de tu período de facturación actual.'
      : 'Are you sure you want to cancel your subscription? You\'ll keep access until the end of your current billing period.';
    if (window.confirm(confirmMsg)) {
      setIsLoading(true);
      try {
        await cancelSubscription();
        toast.success(isSpanish ? 'Suscripción cancelada exitosamente' : 'Subscription cancelled successfully');
        window.location.reload();
      } catch (error) {
        toast.error(error.message || (isSpanish ? 'Error al cancelar' : 'Failed to cancel'));
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await apiFetch('/api/subscription/invite', {
        method: 'POST',
        body: { email: inviteEmail }
      });
      
      if (error) {
        throw new Error(error || 'Failed to send invite');
      }
      
      toast.success(isSpanish ? 'Invitación enviada' : 'Invite sent');
      setInviteEmail('');
      fetchMembers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm(isSpanish ? '¿Eliminar este miembro?' : 'Remove this member?')) return;
    
    try {
      const { data, error } = await apiFetch(`/api/subscription/members/${userId}`, {
        method: 'DELETE'
      });
      
      if (error) {
        throw new Error(error || 'Failed to remove member');
      }
      
      toast.success(isSpanish ? 'Miembro eliminado' : 'Member removed');
      fetchMembers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      const { data, error } = await apiFetch(`/api/subscription/invites/${inviteId}`, {
        method: 'DELETE'
      });
      
      if (!error) {
        toast.success(isSpanish ? 'Invitación cancelada' : 'Invite cancelled');
        fetchMembers();
      }
    } catch (error) {
      toast.error('Failed to cancel invite');
    }
  };

  const handleAcceptInvite = async (inviteId) => {
    try {
      const { data, error } = await apiFetch(`/api/subscription/accept-invite/${inviteId}`, {
        method: 'POST'
      });
      
      if (error) {
        throw new Error(error || 'Failed to accept invite');
      }
      
      toast.success(isSpanish ? '¡Te has unido a la suscripción!' : 'You\'ve joined the subscription!');
      fetchStatus();
      fetchMyInvites();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Generate invite message for text/share
  const getInviteMessage = (shareLink = null) => {
    const inviteUrl = shareLink || window.location.origin;
    
    if (isSpanish) {
      return `¡Hola! Te invito a unirte a mi grupo VIP de ClearCommunicationApp. Es una app increíble que ayuda a entender los significados implícitos en la comunicación. Únete aquí: ${inviteUrl}`;
    }
    return `Hey! I'm inviting you to join my ClearCommunicationApp VIP group. It's an awesome app that helps decode the hidden meanings in everyday communication. Join here: ${inviteUrl}`;
  };

  // Handle text/SMS invite
  const handleTextInvite = async () => {
    try {
      const { data, error } = await apiFetch('/api/subscription/create-share-link', {
        method: 'POST'
      });
      
      let shareLink = window.location.origin;
      if (!error && data) {
        shareLink = data.link;
      }
      
      const message = getInviteMessage(shareLink);
      const encodedMessage = encodeURIComponent(message);
      
      // Try Web Share API first (works on mobile)
      if (navigator.share) {
        navigator.share({
          title: 'ClearCommunicationApp Invitation',
          text: message,
          url: shareLink
        }).catch(() => {
          window.location.href = `sms:?body=${encodedMessage}`;
        });
      } else {
        window.location.href = `sms:?body=${encodedMessage}`;
      }
      
      toast.success(isSpanish ? 'Abriendo aplicación de mensajes...' : 'Opening messaging app...');
    } catch (error) {
      toast.error('Failed to create invite link');
    }
  };

  // Handle share invite (for other apps)
  const handleShareInvite = async () => {
    try {
      const { data, error } = await apiFetch('/api/subscription/create-share-link', {
        method: 'POST'
      });
      
      let shareLink = window.location.origin;
      if (!error && data) {
        shareLink = data.link;
      }
      
      const message = getInviteMessage(shareLink);
      
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'ClearCommunicationApp Invitation',
            text: message,
            url: shareLink
          });
          toast.success(isSpanish ? '¡Compartido!' : 'Shared!');
        } catch (err) {
          // User cancelled or share failed
          if (err.name !== 'AbortError') {
            // Copy to clipboard as fallback
            await navigator.clipboard.writeText(`${message}\n\n${shareLink}`);
            toast.success(isSpanish ? 'Enlace copiado al portapapeles' : 'Link copied to clipboard');
          }
        }
      } else {
        // Copy to clipboard
        await navigator.clipboard.writeText(`${message}\n\n${shareLink}`);
        toast.success(isSpanish ? 'Enlace copiado al portapapeles' : 'Link copied to clipboard');
      }
    } catch (error) {
      toast.error('Failed to create invite link');
    }
  };

  const features = isSpanish ? [
    'Acceso ilimitado a más de 3,400 frases',
    'Todas las categorías y jerga regional',
    'Navegación de frases relacionadas',
    'Funcionalidad de búsqueda completa',
    'Nuevas frases agregadas regularmente'
  ] : [
    'Unlimited access to 3,400+ phrases',
    'All categories and regional slang',
    'Related phrases navigation',
    'Full search functionality',
    'New phrases added regularly'
  ];

  const currentStatus = subscriptionStatus?.status || 'free';
  const remainingViews = getRemainingFreeViews();
  const hasUsedTrial = subscriptionStatus?.trial_ends_at != null;
  const currentTier = subscriptionStatus?.tier || 'individual';
  const isOwner = subscriptionStatus?.is_owner !== false;
  const maxUsers = subscriptionStatus?.max_users || 1;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container py-12">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl font-bold mb-4">
              {isSpanish ? 'Desbloquea Acceso Completo' : 'Unlock Full Access'}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {isSpanish 
                ? 'Obtén acceso ilimitado a todas las frases, categorías y funciones para ayudarte a decodificar la comunicación cotidiana.'
                : 'Get unlimited access to all phrases, categories, and features to help you decode everyday communication.'}
            </p>
          </div>

          {/* Pending Invites */}
          {myInvites.length > 0 && (
            <Alert className="mb-8 border-green-500 bg-green-50">
              <Gift className="h-4 w-4 text-green-600" />
              <AlertDescription>
                <strong>{isSpanish ? '¡Tienes invitaciones pendientes!' : 'You have pending invitations!'}</strong>
                <div className="mt-2 space-y-2">
                  {myInvites.map(invite => (
                    <div key={invite.invite_id} className="flex items-center justify-between bg-white p-2 rounded">
                      <span className="text-sm">
                        {invite.owner_name} ({invite.owner_email}) {isSpanish ? 'te invitó a unirte' : 'invited you to join'}
                      </span>
                      <Button size="sm" onClick={() => handleAcceptInvite(invite.invite_id)}>
                        {isSpanish ? 'Aceptar' : 'Accept'}
                      </Button>
                    </div>
                  ))}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Current Status */}
          {isAuthenticated && (
            <Card className="mb-8">
              <CardContent className="py-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">{isSpanish ? 'Plan Actual' : 'Current Plan'}</p>
                    <div className="flex items-center gap-2">
                      <Badge variant={currentStatus === 'active' ? 'default' : currentStatus === 'trial' ? 'secondary' : currentStatus === 'admin' ? 'destructive' : 'outline'}>
                        {currentStatus === 'admin' ? 'Admin' :
                         currentStatus === 'active' ? (subscriptionStatus?.tier_name || t('vipAccess')) : 
                         currentStatus === 'trial' ? (isSpanish ? 'Prueba Gratis' : 'Free Trial') : 
                         currentStatus === 'expired' ? (isSpanish ? 'Expirado' : 'Expired') : (isSpanish ? '2 Frases Gratis Diarias' : '2 Free Daily Phrases')}
                      </Badge>
                      {currentStatus === 'free' && (
                        <span className="text-sm text-muted-foreground">
                          {remainingViews} {isSpanish ? 'de' : 'of'} {FREE_PHRASE_LIMIT} {t('freePhrasesToday')}
                        </span>
                      )}
                      {!isOwner && subscriptionStatus?.owner_email && (
                        <span className="text-sm text-muted-foreground">
                          ({isSpanish ? 'via' : 'via'} {subscriptionStatus.owner_email})
                        </span>
                      )}
                    </div>
                  </div>
                  {subscriptionStatus?.trial_ends_at && currentStatus === 'trial' && (
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">{isSpanish ? 'Prueba termina' : 'Trial ends'}</p>
                      <p className="font-medium">
                        {new Date(subscriptionStatus.trial_ends_at).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  {subscriptionStatus?.subscription_ends_at && currentStatus === 'active' && (
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">{isSpanish ? 'Se renueva el' : 'Renews on'}</p>
                      <p className="font-medium">
                        {new Date(subscriptionStatus.subscription_ends_at).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Promo Code Section */}
          {isAuthenticated && (currentStatus === 'free' || currentStatus === 'trial' || currentStatus === 'expired') && (
            <Card className="mb-8 border-primary/30 bg-gradient-to-r from-primary/5 to-transparent">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Ticket className="h-5 w-5 text-primary" />
                  {isSpanish ? '¿Tienes un código promocional?' : 'Have a Promo Code?'}
                </CardTitle>
                <CardDescription>
                  {isSpanish 
                    ? 'Ingresa tu código para obtener acceso VIP gratis'
                    : 'Enter your code to get free VIP access'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    placeholder={isSpanish ? "Ingresa el código" : "Enter promo code"}
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCode(e.target.value.toUpperCase());
                      setPromoValidation(null);
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && handleValidatePromo()}
                    className="font-mono uppercase"
                    data-testid="promo-code-input"
                  />
                  {!promoValidation?.valid ? (
                    <Button 
                      onClick={handleValidatePromo} 
                      disabled={!promoCode.trim() || isValidatingPromo}
                      variant="outline"
                    >
                      {isValidatingPromo ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        isSpanish ? 'Validar' : 'Validate'
                      )}
                    </Button>
                  ) : (
                    <Button 
                      onClick={handleApplyPromo} 
                      disabled={isApplyingPromo}
                      className="bg-primary"
                      data-testid="promo-code-apply-btn"
                    >
                      {isApplyingPromo ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Gift className="h-4 w-4 mr-2" />
                      )}
                      {isSpanish ? 'Aplicar' : 'Apply'}
                    </Button>
                  )}
                </div>
                
                {/* Validation Result */}
                {promoValidation && (
                  <div className={`mt-3 p-3 rounded-lg flex items-center gap-2 ${
                    promoValidation.valid 
                      ? 'bg-green-500/10 text-green-400 border border-green-500/20' 
                      : 'bg-red-500/10 text-red-400 border border-red-500/20'
                  }`}>
                    {promoValidation.valid ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      <X className="h-5 w-5" />
                    )}
                    <span>{promoValidation.message}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Member Management - Only for owners with group plans */}
          {isAuthenticated && isOwner && currentStatus !== 'free' && currentStatus !== 'admin' && maxUsers > 1 && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  {isSpanish ? 'Mi Grupo VIP' : 'My VIP Group'}
                </CardTitle>
                <CardDescription>
                  {subscriptionStatus?.current_users || 1} / {maxUsers} {isSpanish ? 'usuarios' : 'users'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Current Members */}
                <div className="space-y-2 mb-4">
                  {members.map(member => (
                    <div key={member.user_id} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                      <div>
                        <p className="font-medium">{member.name}</p>
                        <p className="text-sm text-muted-foreground">{member.email}</p>
                      </div>
                      {member.is_owner ? (
                        <Badge>{isSpanish ? 'Propietario' : 'Owner'}</Badge>
                      ) : (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleRemoveMember(member.user_id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Pending Invites */}
                {pendingInvites.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium mb-2">{isSpanish ? 'Invitaciones Pendientes' : 'Pending Invites'}</p>
                    {pendingInvites.map(invite => (
                      <div key={invite.invite_id} className="flex items-center justify-between p-2 bg-yellow-50 rounded">
                        <span className="text-sm">{invite.email}</span>
                        <Button variant="ghost" size="sm" onClick={() => handleCancelInvite(invite.invite_id)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Invite Form */}
                {(subscriptionStatus?.current_users || 1) < maxUsers && (
                  <div className="space-y-3">
                    {/* Email Invite */}
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder={isSpanish ? 'Correo electrónico' : 'Email address'}
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                      />
                      <Button onClick={handleInvite} disabled={isLoading || !inviteEmail}>
                        <Mail className="h-4 w-4 mr-2" />
                        {isSpanish ? 'Email' : 'Email'}
                      </Button>
                    </div>
                    
                    {/* Text/Share Invite Options */}
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={handleTextInvite}
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        {isSpanish ? 'Invitar por Texto' : 'Text Invite'}
                      </Button>
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={handleShareInvite}
                      >
                        <Share2 className="h-4 w-4 mr-2" />
                        {isSpanish ? 'Compartir' : 'Share'}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground text-center">
                      {isSpanish 
                        ? 'Invita a tu grupo VIP por email, texto o comparte el enlace.'
                        : 'Invite to your VIP group via email, text, or share the link.'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Pricing Cards — Apple IAP on iOS, Stripe checkout elsewhere */}
          {(currentStatus === 'free' || currentStatus === 'expired') && (
            <>
              {isAppleStoreContext ? (
                <IapPurchaseBlock
                  isSpanish={isSpanish}
                  userId={user?.user_id || null}
                />
              ) : (
              <>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {Object.entries(TIERS).map(([tierId, tier]) => {
                  const TierIcon = tier.icon;
                  const isPopular = tierId === 'family';
                  
                  return (
                    <Card 
                      key={tierId} 
                      className={`relative ${isPopular ? 'border-primary border-2' : ''} ${selectedTier === tierId ? 'ring-2 ring-primary' : ''}`}
                      onClick={() => setSelectedTier(tierId)}
                    >
                      {isPopular && (
                        <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-primary">
                          {isSpanish ? 'Popular' : 'Popular'}
                        </Badge>
                      )}
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <TierIcon className="h-5 w-5 text-yellow-500" />
                          {isSpanish ? tier.nameEs : tier.name}
                        </CardTitle>
                        <CardDescription className="text-xs">
                          {isSpanish ? tier.descriptionEs : tier.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pb-2">
                        <div className="mb-2">
                          <span className="text-2xl font-bold">${tier.price}</span>
                          <span className="text-muted-foreground text-sm">/{isSpanish ? 'mes' : 'mo'}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {tier.maxUsers === 1 
                            ? (isSpanish ? '1 usuario' : '1 user')
                            : (isSpanish ? `Hasta ${tier.maxUsers} usuarios` : `Up to ${tier.maxUsers} users`)}
                        </p>
                      </CardContent>
                      <CardFooter>
                        <Button 
                          className="w-full" 
                          size="sm"
                          variant={selectedTier === tierId ? 'default' : 'outline'}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSubscribe(tierId);
                          }}
                          disabled={isLoading}
                        >
                          {isLoading && selectedTier === tierId 
                            ? (isSpanish ? 'Procesando...' : 'Processing...') 
                            : (isSpanish ? 'Seleccionar' : 'Select')}
                        </Button>
                      </CardFooter>
                    </Card>
                  );
                })}
              </div>

              {/* Free Trial Notice */}
              {!hasUsedTrial && (
                <Alert className="mb-8 border-green-500 bg-green-50">
                  <Gift className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-700">
                    <strong>{isSpanish ? '¡7 Días Gratis!' : '7 Days Free!'}</strong> {isSpanish 
                      ? 'Todos los planes incluyen una prueba gratuita de 7 días. Se te cobrará el día 8 si no cancelas.'
                      : 'All plans include a 7-day free trial. You\'ll be charged on day 8 if you don\'t cancel.'}
                  </AlertDescription>
                </Alert>
              )}

              {/* Features List */}
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle>{isSpanish ? 'Todas las suscripciones incluyen' : 'All subscriptions include'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="grid md:grid-cols-2 gap-2">
                    {features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-primary flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Business/School Notice */}
              <Alert className="mb-8 border-blue-300 bg-blue-50">
                <Building2 className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-700">
                  <strong>{isSpanish ? 'Suscripciones para Empresas y Escuelas' : 'Business & School Subscriptions'}</strong>
                  <br />
                  {isSpanish 
                    ? 'Opciones de suscripción para empresas y escuelas están disponibles. Por favor contacta a '
                    : 'Business and school subscription options are available. Please contact '}
                  <a href="mailto:Support@ClearCommunicationApp.com" className="font-medium underline">
                    Support@ClearCommunicationApp.com
                  </a>
                  {isSpanish ? ' para más información.' : ' for additional information.'}
                </AlertDescription>
              </Alert>
              </>
              )}
            </>
          )}

          {/* Cancel Button for Active Subscribers */}
          {isAuthenticated && isOwner && (currentStatus === 'active' || currentStatus === 'trial') && (
            <div className="text-center mb-8">
              <Button 
                variant="outline" 
                onClick={handleCancel}
                disabled={isLoading}
                data-testid="cancel-subscription-btn"
              >
                {isLoading 
                  ? (isSpanish ? 'Cancelando...' : 'Cancelling...') 
                  : (isSpanish ? 'Cancelar Suscripción' : 'Cancel Subscription')}
              </Button>
            </div>
          )}

          {/* FAQ */}
          <div className="mt-12">
            <Link to="/faqs" className="hover:text-primary transition-colors">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                {t('faqTitle')}
                <span className="text-sm font-normal text-primary">{isSpanish ? '(ver todas)' : '(view all)'}</span>
              </h2>
            </Link>
            <div className="space-y-2">
              <FAQItem 
                question={isSpanish ? "¿Cuál es la diferencia entre los planes?" : "What's the difference between plans?"}
                answer={isSpanish
                  ? "Todos los planes ofrecen acceso completo a todas las frases. La diferencia es cuántas personas pueden compartir la suscripción. Individual es para 1 persona, Familia y Amigos para hasta 4, Todo el Asado para hasta 8, y Reunión Familiar para hasta 20 personas."
                  : "All plans offer full access to all phrases. The difference is how many people can share the subscription. Individual is for 1 person, Family & Friends for up to 4, The Whole Cookout for up to 8, and Family Reunion for up to 20 people."}
              />
              <FAQItem 
                question={isSpanish ? "¿Cómo agrego personas a mi plan?" : "How do I add people to my plan?"}
                answer={isSpanish
                  ? "Una vez suscrito a un plan grupal, verás una sección 'Administrar Miembros' donde puedes invitar usuarios por correo electrónico. Ellos recibirán una invitación para unirse a tu suscripción."
                  : "Once subscribed to a group plan, you'll see a 'Manage Members' section where you can invite users by email. They'll receive an invitation to join your subscription."}
              />
              <FAQItem 
                question={isSpanish ? "¿Puedo cancelar en cualquier momento?" : "Can I cancel anytime?"}
                answer={isSpanish
                  ? "¡Sí! Puedes cancelar en línea o enviando un correo a Support@ClearCommunicationApp.com. Si cancelas durante la prueba, no se te cobrará."
                  : "Yes! You can cancel online or by emailing Support@ClearCommunicationApp.com. If you cancel during the trial, you won't be charged."}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
